'use client'

import { useEffect, useMemo, useState } from 'react'
import { Camera, Plus, UserIcon } from 'lucide-react'

import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { Media as MediaType, User } from '@/payload-types'

import { ProfileStepButton } from './ProfileStepButton'
import { getProfileStepStatus, isProfileStepComplete } from './profileStepCompletion'

export type ProfileSteps = 'profile' | 'socials' | 'location' | 'profileImage'

export const ProfileStep = ({
  user,
  setCurrentProfileStep,
  setOpenDrawer,
  openDrawer,
  currentProfileStep,
}: {
  user: User
  setCurrentProfileStep: (step: ProfileSteps) => void
  setOpenDrawer: (open: boolean) => void
  openDrawer: boolean
  currentProfileStep: ProfileSteps
}) => {
  const [_showImageUpload, setShowImageUpload] = useState(false)

  const steps = useMemo<
    Array<{
      section: ProfileSteps
      title: string
      description: string
      onClick: () => void
      noButton?: boolean
    }>
  >(
    () => [
      {
        section: 'profile',
        title: 'Profile',
        description: 'Tell us a bit about yourself to help personalize your experience',
        onClick: () => {
          setCurrentProfileStep('profile')
          setOpenDrawer(openDrawer ? false : true)
        },
      },
      {
        section: 'socials',
        title: 'Social',
        description:
          'Connect to your social profiles so people can find and follow you more easily. Only link the platforms',
        onClick: () => {
          setCurrentProfileStep('socials')
          setOpenDrawer(openDrawer ? false : true)
        },
      },
      {
        section: 'location',
        title: 'Location',
        description:
          'Add your location details to tailor content, opportunities, or events available in your area.',
        onClick: () => {
          setCurrentProfileStep('location')
          setOpenDrawer(openDrawer ? false : true)
        },
      },
      {
        section: 'profileImage',
        title: 'Image',
        description: 'Image',
        onClick: () => {
          setCurrentProfileStep('profileImage')
          setShowImageUpload(true)
          setOpenDrawer(openDrawer ? false : true)
        },
        noButton: true,
      },
    ],
    [setCurrentProfileStep, setOpenDrawer, openDrawer],
  )

  // Set initial step to first incomplete only on mount; don't override when user clicks a completed step to edit
  useEffect(() => {
    const incompleteSteps = steps.filter((step) => !isProfileStepComplete(user, step.section))
    if (incompleteSteps.length > 0) {
      setCurrentProfileStep(incompleteSteps[0].section)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount so clicking a completed step opens that form
  }, [])

  return (
    <div className="flex w-full flex-col items-center justify-center p-2 sm:p-4">
      <h2 className="p-4 text-center font-family-display text-4xl">
        Let&apos;s get to know you a bit better
      </h2>
      <div className="flex w-full flex-col items-center justify-center gap-2">
        {/* Profile image display and optional upload button*/}
        <div className="relative mb-4">
          <div className="m-auto size-[150px] overflow-hidden rounded-full">
            {!user.image ? (
              <div className="flex size-full items-center justify-center rounded-full bg-neutral-200 object-cover object-center">
                <UserIcon className="size-20 self-center text-accent" />
              </div>
            ) : (
              <Media
                resource={user.image as MediaType}
                fill={true}
                imgClassName="size-full rounded-full object-cover object-center"
              />
            )}
          </div>
          <Button
            variant="icon"
            className="absolute right-0 bottom-0 rounded-full bg-accent"
            onClick={() => {
              setCurrentProfileStep('profileImage')
              setShowImageUpload(true)
              setOpenDrawer(openDrawer ? false : true)
            }}
          >
            <Camera className="size-5" />
            <Plus className="absolute right-0 -bottom-1 size-4 rounded-full bg-sage-500 p-1 text-primary-inverse" />
          </Button>
        </div>
        {steps
          .filter((step) => !step.noButton)
          .map((step) => (
            <ProfileStepButton
              key={step.section}
              title={step.title}
              description={step.description}
              onClick={step.onClick}
              isActive={currentProfileStep === step.section}
              status={getProfileStepStatus(user, step.section)}
            />
          ))}
      </div>
    </div>
  )
}
